import { useEffect, useState } from 'react';
import { AchievementEarnedSheet } from './components/AchievementEarnedSheet';
import { InstallBanner } from './components/InstallBanner';
import { Toaster } from './components/Toaster';
import { isUsable, useLocation } from './state/location';
import { useAuth } from './state/auth';
import { useLeaderboard } from './state/leaderboard';
import { usePlayer } from './state/player';
import { setForeground, startRealtime, stopRealtime } from './state/realtime';
import { useScan } from './state/scan';
import { SHELL_TABS, type ShellTab, useShell } from './state/shell';
import { HuntScreen } from './screens/HuntScreen';
import { InventoryScreen } from './screens/InventoryScreen';
import { LeaderboardScreen } from './screens/LeaderboardScreen';
import { LocationGateScreen } from './screens/LocationGateScreen';
import { LoginScreen } from './screens/LoginScreen';
import { PlayerProfileScreen } from './screens/PlayerProfileScreen';
import { ProfileScreen } from './screens/ProfileScreen';

/** Gate order: location, then sign-in, then the game. Location is re-checked whenever the page becomes visible. */
export function App() {
  const locationStatus = useLocation((s) => s.status);
  const authStatus = useAuth((s) => s.status);
  const authError = useAuth((s) => s.error);
  const uid = useAuth((s) => s.uid);

  useEffect(() => useAuth.getState().init(), []);
  useEffect(() => useLocation.getState().watch(), []);

  // Everything tied to the signed-in user.
  useEffect(() => {
    if (!uid) {
      stopRealtime();
      usePlayer.getState().reset();
      useLeaderboard.getState().reset();
      useScan.getState().reset();
      useShell.getState().select('hunt');
      return;
    }
    void usePlayer.getState().load();
    startRealtime(uid);
    const onVis = () => setForeground(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      stopRealtime();
    };
  }, [uid]);

  let body;
  if (!isUsable(locationStatus)) body = <LocationGateScreen status={locationStatus} />;
  else if (authStatus === 'loading') body = <div className="center-fill"><span className="spinner dark" role="progressbar" aria-label="Loading" /></div>;
  else if (authStatus === 'error') body = <div className="center-fill">Sign-in unavailable: {authError}</div>;
  else if (authStatus === 'signedOut') body = <LoginScreen />;
  else body = <AppShell />;

  return (
    <div className="app">
      <UpdateBanner />
      {body}
      <Toaster />
    </div>
  );
}

function UpdateBanner() {
  const apply = useShell((s) => s.applyUpdate);
  if (!apply) return null;
  return (
    <div role="status" style={{ background: 'var(--lemon)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', fontWeight: 800 }}>
      A new version is ready.
      <button type="button" className="btn filled" style={{ padding: '6px 16px', fontSize: 14 }} onClick={apply}>Reload</button>
    </div>
  );
}

const SCREENS: Record<ShellTab, () => React.JSX.Element> = {
  hunt: HuntScreen,
  collection: InventoryScreen,
  ranks: LeaderboardScreen,
  you: ProfileScreen,
};

const NAV: { tab: ShellTab; icon: string; label: string }[] = [
  { tab: 'hunt', icon: '📡', label: 'Hunt' },
  { tab: 'collection', icon: '🎒', label: 'Collection' },
  { tab: 'ranks', icon: '🏆', label: 'Ranks' },
  { tab: 'you', icon: '🙂', label: 'You' },
];

/** Bottom navigation over the four tabs. A tab is built the first time it is shown and then kept alive. */
export function AppShell() {
  const tab = useShell((s) => s.tab);
  const select = useShell((s) => s.select);
  const profile = useShell((s) => s.profile);
  const [visited, setVisited] = useState<Set<ShellTab>>(() => new Set([tab]));

  useEffect(() => {
    setVisited((v) => (v.has(tab) ? v : new Set(v).add(tab)));
  }, [tab]);

  return (
    <>
      <main style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {SHELL_TABS.map((t) => {
          const Screen = SCREENS[t];
          return (
            <div key={t} hidden={t !== tab} style={{ flex: 1, minHeight: 0, display: t === tab ? 'flex' : 'none', flexDirection: 'column' }}>
              {(visited.has(t) || t === tab) && <Screen />}
            </div>
          );
        })}
        {profile && <PlayerProfileScreen key={profile.playerId} {...profile} />}
      </main>
      <InstallBanner />
      <AchievementEarnedSheet />
      <nav className="nav" aria-label="Main">
        {NAV.map((n) => (
          <button key={n.tab} type="button" aria-current={n.tab === tab ? 'page' : undefined} onClick={() => select(n.tab)}>
            <span className="pill" aria-hidden="true">{n.icon}</span>
            {n.label}
          </button>
        ))}
      </nav>
    </>
  );
}
